
import { GoogleGenAI, Type } from "@google/genai";
import { HealthLog, PredictionResult } from "../types";

export class HealthAIService {
  private ai: GoogleGenAI;

  constructor() {
    const getApiKey = (): string => {
      // @ts-ignore
      if (typeof import.meta !== 'undefined' && import.meta.env) {
        // @ts-ignore
        return import.meta.env.VITE_API_KEY || import.meta.env.API_KEY || '';
      }
      return process.env.VITE_API_KEY || process.env.API_KEY || '';
    };

    const apiKey = getApiKey();
    this.ai = new GoogleGenAI({ apiKey });
  }

  async predictFutureHealth(currentLogs: HealthLog[], disciplineScore: number): Promise<PredictionResult> {
    const logsDescription = currentLogs.map(log => 
      `วันที่: ${log.date}, น้ำหนัก: ${log.weight}kg, BMI: ${log.bmi?.toFixed(1)}, ความดัน: ${log.systolic}/${log.diastolic}mmHg, FBS: ${log.fbs || 'N/A'}, TG: ${log.triglyceride}, Chol: ${log.cholesterol}, HDL: ${log.hdl}, LDL: ${log.ldl}, AST: ${log.ast || 'N/A'}, ALT: ${log.alt || 'N/A'}`
    ).join('\n');

    const latest = currentLogs[currentLogs.length - 1];
    
    const prompt = `
      Act as a specialized Health Data Scientist specializing in metabolic health and NCDs (Non-Communicable Diseases).
      
      Historical Health Trends (Thai Context):
      ${logsDescription}

      Current Discipline Context:
      - Latest Status: Weight ${latest.weight}kg, FBS ${latest.fbs || 'N/A'}mg/dL, Lipid (LDL ${latest.ldl}, TG ${latest.triglyceride}), Liver Enzymes (AST ${latest.ast || 'N/A'}, ALT ${latest.alt || 'N/A'}).
      - Projected Discipline Score for next 3 months: ${disciplineScore}/100.
      (100 = strict adherence to healthy diet, regular exercise, and medication; 0 = complete non-adherence).

      Task:
      Analyze metabolic trends, sugar control (FBS), lipid profile, and liver health (AST/ALT) over the provided dates. Extrapolate health results 3 months into the future based on the user's discipline. 
      Specifically predict: Weight, BP (Sys/Dia), TG, Total Cholesterol, HDL, and LDL.
      
      Return the response in JSON format.
    `;

    const response = await this.ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            futureWeight: { type: Type.NUMBER },
            futureSystolic: { type: Type.NUMBER },
            futureDiastolic: { type: Type.NUMBER },
            futureCholesterol: { type: Type.NUMBER },
            futureTriglyceride: { type: Type.NUMBER },
            futureHDL: { type: Type.NUMBER },
            futureLDL: { type: Type.NUMBER },
            aiAdvice: { type: Type.STRING, description: "Detailed medical analysis in Thai language including specific analysis of sugar, lipid, and liver enzyme trends." },
            riskLevel: { type: Type.STRING, enum: ["ต่ำ", "ปานกลาง", "สูง"] }
          },
          required: ["futureWeight", "futureSystolic", "futureDiastolic", "futureCholesterol", "futureTriglyceride", "futureHDL", "futureLDL", "aiAdvice", "riskLevel"]
        }
      }
    });

    try {
      const text = response.text;
      if (!text) throw new Error("Empty AI response");
      return JSON.parse(text);
    } catch (e) {
      console.error("Failed to parse AI response", e);
      throw new Error("AI prediction failed");
    }
  }
}
